import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, Input, Select } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { useGetCannedTallyLengthsForCategory } from '~/api/canned-tally';
import { useGetLocations } from '~/api/location';
import { useGetUserPreferences, useUpdateUserPreferences } from '~/api/auth';
import { useEnsurePlatformProductLength } from '~/api/platformProducts';
import {
  useUpsertRequestForQuote,
  useAddRequestForQuoteItems,
  useGetDraftRequestForQuote,
} from '~/api/requestForQuote';
import { priceUnitLabel, toastGraphQLError } from '~/utils';
import { Currency, type Quantity, type UnitCode } from '~/graphql/graphql';
import { RandomTallyBundlesGrid } from '~/components/common/RandomTallyBundlesGrid';
import { hasInvalidBundleCount } from '~/components/common/tallyBundleCounts';
import { SelectSellersForRandomTallyModal } from './SelectSellersForRandomTallyModal';

type RandomTallyItemRow = {
  lengthId: string;
  platformProductLengthId?: string;
  quantityInBundles: number;
  piecesPerBundle: number;
  pickupPrice?: { amount: number; currency: Currency };
  deliveryPrice?: { amount: number; currency: Currency };
};

type CreateRandomTallyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  platformProductId: string;
  productName: string;
  standardUnitsCode: UnitCode;
  shippingLocationId: string | null | undefined;
  excludeOrganizationIds: string[];
  piecesPerBundle: Quantity;
};

const CURRENCY_OPTIONS = Object.values(Currency).map((currency) => ({
  label: currency,
  value: currency,
}));

export function CreateRandomTallyModal({
  isOpen,
  onClose,
  platformProductId,
  productName,
  standardUnitsCode,
  shippingLocationId,
  excludeOrganizationIds,
  piecesPerBundle,
}: Readonly<CreateRandomTallyModalProps>) {
  const { t } = useTranslation(['order-builder', 'order']);

  const { data: candidateLengthsData } =
    useGetCannedTallyLengthsForCategory(platformProductId);
  const { data: locationsData } = useGetLocations();
  const shipToName = locationsData?.locations?.find(
    (location) => location.id === shippingLocationId
  )?.name;
  const candidateLengths =
    candidateLengthsData?.cannedTallyLengthsForCategory ?? [];

  const [currency, setCurrency] = useState<Currency>(Currency.Usd);
  const [bundlePickupPrice, setBundlePickupPrice] = useState('');
  const [bundleDeliveryPrice, setBundleDeliveryPrice] = useState('');
  const { data: preferencesData } = useGetUserPreferences();
  const { mutate: updatePreferences } = useUpdateUserPreferences();
  const [useBundlePriceChoice, setUseBundlePriceChoice] = useState<
    boolean | null
  >(null);
  const useBundlePrice =
    useBundlePriceChoice ??
    preferencesData?.userPreferences?.randomTallyUseTallyPrice ??
    false;
  const [quantitiesByLengthId, setQuantitiesByLengthId] = useState<
    Record<string, string>
  >({});
  const [pickupPricesByLengthId, setPickupPricesByLengthId] = useState<
    Record<string, string>
  >({});
  const [deliveryPricesByLengthId, setDeliveryPricesByLengthId] = useState<
    Record<string, string>
  >({});
  const [leadTimeDays, setLeadTimeDays] = useState('');

  const [
    sellerSelectionRequestForQuoteId,
    setSellerSelectionRequestForQuoteId,
  ] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: upsertRequestForQuote } = useUpsertRequestForQuote();
  const { mutateAsync: addRequestForQuoteItems } = useAddRequestForQuoteItems();
  const { mutateAsync: ensurePlatformProductLength } =
    useEnsurePlatformProductLength();

  const { data: draftData } = useGetDraftRequestForQuote(
    platformProductId,
    shippingLocationId ?? undefined
  );
  const draftRequestForQuote = draftData?.draftRequestForQuote;
  const hasAppliedDraftRef = useRef(false);

  useEffect(() => {
    if (
      !draftRequestForQuote ||
      hasAppliedDraftRef.current ||
      sellerSelectionRequestForQuoteId
    )
      return;
    hasAppliedDraftRef.current = true;

    const quantities: Record<string, string> = {};
    const pickupPrices: Record<string, string> = {};
    const deliveryPrices: Record<string, string> = {};
    let draftCurrency: Currency | undefined;

    for (const item of draftRequestForQuote.items) {
      const lengthId = item.platformProductsLength.length.id;
      quantities[lengthId] = String(
        Math.round(item.quantityInPieces / piecesPerBundle.value)
      );
      if (item.pickupPrice) {
        pickupPrices[lengthId] = String(item.pickupPrice.amount);
        draftCurrency = item.pickupPrice.currency;
      }
      if (item.deliveryPrice) {
        deliveryPrices[lengthId] = String(item.deliveryPrice.amount);
        draftCurrency = draftCurrency ?? item.deliveryPrice.currency;
      }
    }

    setQuantitiesByLengthId(quantities);
    setPickupPricesByLengthId(pickupPrices);
    setDeliveryPricesByLengthId(deliveryPrices);
    setUseBundlePriceChoice(draftRequestForQuote.useTallyPrice);
    if (draftRequestForQuote.pickupPriceForTally) {
      setBundlePickupPrice(
        String(draftRequestForQuote.pickupPriceForTally.amount)
      );
      draftCurrency = draftRequestForQuote.pickupPriceForTally.currency;
    }
    if (draftRequestForQuote.deliveryPriceForTally) {
      setBundleDeliveryPrice(
        String(draftRequestForQuote.deliveryPriceForTally.amount)
      );
      draftCurrency =
        draftCurrency ?? draftRequestForQuote.deliveryPriceForTally.currency;
    }
    if (draftRequestForQuote.leadTimeDays != null) {
      setLeadTimeDays(String(draftRequestForQuote.leadTimeDays));
    }
    if (draftCurrency) {
      setCurrency(draftCurrency);
    }
    toast.success(t('order_builder.create_random_tally.draft_loaded'));
  }, [
    draftRequestForQuote,
    sellerSelectionRequestForQuoteId,
    piecesPerBundle.value,
    t,
  ]);

  const handleUseBundlePriceChange = (checked: boolean) => {
    setUseBundlePriceChoice(checked);
    updatePreferences({ preferences: { randomTallyUseTallyPrice: checked } });
  };

  const parsePrice = (value: string | undefined) => {
    const amount = parseFloat(value ?? '');
    return Number.isFinite(amount) && amount > 0
      ? { amount, currency }
      : undefined;
  };

  const parsedLeadTimeDays = (() => {
    const parsed = parseInt(leadTimeDays, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  })();

  const buildItemRows = (): RandomTallyItemRow[] =>
    candidateLengths
      .map((candidate) => ({
        candidate,
        quantityInBundles: Number(
          quantitiesByLengthId[candidate.length.id] ?? ''
        ),
      }))
      .filter(
        ({ quantityInBundles }) =>
          Number.isInteger(quantityInBundles) && quantityInBundles > 0
      )
      .map(({ candidate, quantityInBundles }) => ({
        lengthId: candidate.length.id,
        platformProductLengthId: candidate.platformProductLength?.id,
        quantityInBundles,
        piecesPerBundle: piecesPerBundle.value,
        pickupPrice: useBundlePrice
          ? undefined
          : parsePrice(pickupPricesByLengthId[candidate.length.id]),
        deliveryPrice: useBundlePrice
          ? undefined
          : parsePrice(deliveryPricesByLengthId[candidate.length.id]),
      }));

  const bundlePickupPriceValue = parsePrice(bundlePickupPrice);
  const bundleDeliveryPriceValue = parsePrice(bundleDeliveryPrice);

  const itemRows = buildItemRows();
  const hasNoQuantities = itemRows.length === 0;
  const hasInvalidQuantities = hasInvalidBundleCount(
    quantitiesByLengthId,
    candidateLengths.map((candidate) => candidate.length.id)
  );

  const saveDraft = async (resolvedShippingLocationId: string) => {
    hasAppliedDraftRef.current = true;
    const created = await upsertRequestForQuote({
      tallyPlatformProductId: platformProductId,
      leadTimeDays: parsedLeadTimeDays,
      shippingLocationId: resolvedShippingLocationId,
      useTallyPrice: useBundlePrice,
      pickupPriceForTally: useBundlePrice ? bundlePickupPriceValue : undefined,
      deliveryPriceForTally: useBundlePrice
        ? bundleDeliveryPriceValue
        : undefined,
    });
    const requestForQuoteId = created.upsertRequestForQuote.id;

    const resolvedItems = await Promise.all(
      itemRows.map(async (item) => {
        let platformProductLengthId = item.platformProductLengthId;
        if (!platformProductLengthId) {
          const ensured = await ensurePlatformProductLength({
            platformProductId,
            lengthId: item.lengthId,
          });
          platformProductLengthId = ensured.ensurePlatformProductLength.id;
        }
        return {
          platformProductLengthId,
          numberOfBundles: item.quantityInBundles,
          piecesPerBundle: item.piecesPerBundle,
          pickupPrice: item.pickupPrice,
          deliveryPrice: item.deliveryPrice,
        };
      })
    );

    await addRequestForQuoteItems({
      requestForQuoteId,
      items: resolvedItems,
      replace: true,
    });

    return requestForQuoteId;
  };

  const handleSubmitToSellers = async () => {
    if (!shippingLocationId) {
      toast.error(
        t('order_builder.create_random_tally.shipping_address_required')
      );
      return;
    }
    if (hasNoQuantities) {
      toast.error(
        t('order_builder.create_random_tally.at_least_one_item_required')
      );
      return;
    }

    setIsSaving(true);
    try {
      const requestForQuoteId = await saveDraft(shippingLocationId);
      toast.success(t('order_builder.create_random_tally.save_success'));
      setSellerSelectionRequestForQuoteId(requestForQuoteId);
    } catch (error) {
      toastGraphQLError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!shippingLocationId) {
      toast.error(
        t('order_builder.create_random_tally.shipping_address_required')
      );
      return;
    }
    if (hasNoQuantities) {
      toast.error(
        t('order_builder.create_random_tally.at_least_one_item_required')
      );
      return;
    }

    setIsSaving(true);
    try {
      await saveDraft(shippingLocationId);
      toast.success(t('order_builder.create_random_tally.save_success'));
      onClose();
    } catch (error) {
      toastGraphQLError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !sellerSelectionRequestForQuoteId}
        onClose={onClose}
      >
        <div className="bg-neut-light rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
          <ModalHeader
            title={t('order_builder.create_random_tally.title')}
            description={
              <>
                {t('order_builder.create_random_tally.product_label')}:{' '}
                {productName}
                {shipToName && (
                  <>
                    {' · '}
                    {t('order_builder.create_random_tally.ship_to_label')}:{' '}
                    {shipToName}
                  </>
                )}
              </>
            }
            onClose={onClose}
          />

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="flex flex-col gap-4 mb-6">
              <SectionPanel
                title={t(
                  'order_builder.create_random_tally.request_details_heading'
                )}
              >
                <div className="flex gap-4">
                  <div className="max-w-[200px]">
                    <Select
                      name="currency"
                      label={t(
                        'order_builder.create_random_tally.currency_label'
                      )}
                      value={currency}
                      onChange={(_, value) => setCurrency(value as Currency)}
                      options={CURRENCY_OPTIONS}
                    />
                  </div>
                  <div className="max-w-[160px]">
                    <Input
                      name="lead-time-days"
                      type="number"
                      min={0}
                      step="1"
                      label={t(
                        'order_builder.create_random_tally.lead_time_column'
                      )}
                      value={leadTimeDays}
                      onChange={(_, value) => setLeadTimeDays(value)}
                    />
                  </div>
                </div>
              </SectionPanel>

              <SectionPanel
                title={t('order_builder.create_random_tally.pricing_heading')}
              >
                <label className="flex items-center gap-3 text-sm text-neut-prim whitespace-nowrap cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-5 w-5 cursor-pointer"
                    checked={useBundlePrice}
                    onChange={(event) =>
                      handleUseBundlePriceChange(event.target.checked)
                    }
                  />
                  {t(
                    'order_builder.create_random_tally.use_bundle_price_label'
                  )}
                </label>
                <p className="text-xs text-neut-sec mt-1">
                  {t('order_builder.create_random_tally.use_bundle_price_hint')}
                </p>
                {useBundlePrice && (
                  <div className="flex gap-4 mt-3">
                    <div className="max-w-[160px]">
                      <Input
                        name="bundle-pickup-price"
                        type="number"
                        min={0}
                        step="0.01"
                        label={t(
                          'order_builder.create_random_tally.bundle_pickup_price_label'
                        )}
                        value={bundlePickupPrice}
                        onChange={(_, value) => setBundlePickupPrice(value)}
                      />
                    </div>
                    <div className="max-w-[160px]">
                      <Input
                        name="bundle-delivery-price"
                        type="number"
                        min={0}
                        step="0.01"
                        label={t(
                          'order_builder.create_random_tally.bundle_delivery_price_label'
                        )}
                        value={bundleDeliveryPrice}
                        onChange={(_, value) => setBundleDeliveryPrice(value)}
                      />
                    </div>
                  </div>
                )}
              </SectionPanel>
            </div>

            <h3 className="text-sm font-semibold text-neut-prim mb-1">
              {t('order_builder.create_random_tally.items_heading')}
            </h3>
            <p className="text-xs text-neut-sec mb-3">
              {t('order_builder.create_random_tally.items_subheading')}
            </p>
            <RandomTallyBundlesGrid
              platformProductId={platformProductId}
              bundleCountsByLengthId={quantitiesByLengthId}
              onBundleCountChange={(lengthId, value) =>
                setQuantitiesByLengthId((previous) => ({
                  ...previous,
                  [lengthId]: value,
                }))
              }
              piecesPerBundle={piecesPerBundle}
              extraColumnHeaders={
                useBundlePrice ? undefined : (
                  <>
                    <th className="font-normal pb-2 pl-3 w-32">
                      {t(
                        'order_builder.create_random_tally.pickup_price_column'
                      )}
                      <br />
                      <span className="font-normal normal-case text-neut-400">
                        $/{priceUnitLabel(standardUnitsCode)}
                      </span>
                    </th>
                    <th className="font-normal pb-2 pl-3 w-32">
                      {t(
                        'order_builder.create_random_tally.delivery_price_column'
                      )}
                      <br />
                      <span className="font-normal normal-case text-neut-400">
                        $/{priceUnitLabel(standardUnitsCode)}
                      </span>
                    </th>
                  </>
                )
              }
              renderExtraColumns={
                useBundlePrice
                  ? undefined
                  : (lengthId) => (
                      <>
                        <td className="py-2 pl-3">
                          <Input
                            name={`${lengthId}-pickup-price`}
                            type="number"
                            min={0}
                            step="0.01"
                            containerClassName="min-w-0"
                            value={pickupPricesByLengthId[lengthId] ?? ''}
                            onChange={(_, value) =>
                              setPickupPricesByLengthId((previous) => ({
                                ...previous,
                                [lengthId]: value,
                              }))
                            }
                          />
                        </td>
                        <td className="py-2 pl-3">
                          <Input
                            name={`${lengthId}-delivery-price`}
                            type="number"
                            min={0}
                            step="0.01"
                            containerClassName="min-w-0"
                            value={deliveryPricesByLengthId[lengthId] ?? ''}
                            onChange={(_, value) =>
                              setDeliveryPricesByLengthId((previous) => ({
                                ...previous,
                                [lengthId]: value,
                              }))
                            }
                          />
                        </td>
                      </>
                    )
              }
            />
          </div>

          <div className="px-6 py-4 border-t border-neut-border flex items-center justify-end gap-3 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              width="auto"
              onClick={onClose}
              disabled={isSaving}
            >
              {t('order_builder.create_random_tally.cancel')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              width="auto"
              onClick={handleSave}
              disabled={hasNoQuantities || hasInvalidQuantities || isSaving}
              loading={isSaving}
            >
              {t('order_builder.create_random_tally.save')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              width="auto"
              onClick={handleSubmitToSellers}
              disabled={hasNoQuantities || hasInvalidQuantities || isSaving}
              loading={isSaving}
            >
              {t('order_builder.create_random_tally.select_sellers')}
            </Button>
          </div>
        </div>
      </Modal>

      {sellerSelectionRequestForQuoteId && shippingLocationId && (
        <SelectSellersForRandomTallyModal
          isOpen={true}
          onClose={() => setSellerSelectionRequestForQuoteId(null)}
          onSubmitted={onClose}
          platformProductId={platformProductId}
          shippingLocationId={shippingLocationId}
          excludeOrganizationIds={excludeOrganizationIds}
          requestForQuoteId={sellerSelectionRequestForQuoteId}
        />
      )}
    </>
  );
}
